import React, { useState, useContext, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Divider, 
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Avatar,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { format } from 'date-fns';
import { HttpRequestContext } from '../../Blocks/HttpRequestProvider';
import BillingProfileForm from './Blocks/BillingProfileForm';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const InvoiceHeader = ({ 
  invoiceData, 
  setInvoiceData, 
  viewMode, 
  setHasUnsavedChanges,
  setIsClientDialogOpen,
  onBillingProfileChange,
  onStatusChange 
}) => {
  const { sendRequest } = useContext(HttpRequestContext);
  // State for dialog controls
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [invoiceDetailsDialogOpen, setInvoiceDetailsDialogOpen] = useState(false);
  
  // Initialize with default values but they'll be overridden by useEffect
  const [companyInfo, setCompanyInfo] = useState({
    name: "",
    address: "",
    city: "",
    vatNumber: "",
    bceNumber: "",
    iban: "",
    bic: ""
  });

  // Update company info when invoiceData changes
  useEffect(() => {
    // Check if we have billing profile data or provider information from the API
    if (invoiceData.providerInformation) {
      setCompanyInfo({
        name: invoiceData.providerInformation?.company_name || "",
        address: invoiceData.providerInformation?.address_line1 || "",
        city: invoiceData.providerInformation?.address_line2 || "",
        vatNumber: invoiceData.providerInformation?.vat_number || "",
        bceNumber: invoiceData.providerInformation?.bce_number || "",
        iban: invoiceData.providerInformation?.iban || "",
        bic: invoiceData.providerInformation?.bic || ""
      });
    }
  }, [invoiceData]);

  // Determine watermark display based on invoice status
  const showWatermark = true; // Show watermark for all statuses
  const getWatermarkText = () => {
    switch(invoiceData.invoiceStatus) {
      case 'Brouillon': return 'BROUILLON';
      case 'Envoyée': return 'ENVOYÉE';
      case 'Payée': return 'PAYÉE';
      case 'Annulée': return 'ANNULÉE';
      default: return '';
    }
  };
  const watermarkText = getWatermarkText();
  
  // Status options for the invoice
  const invoiceStatusOptions = [
    { value: 'Brouillon', label: 'Brouillon' },
    { value: 'Envoyée', label: 'Envoyée' },
    { value: 'Payée', label: 'Payée' },
    { value: 'Annulée', label: 'Annulée' }
  ];
  
  // Handle company profile save
  const handleCompanyProfileSave = (billingProfileData) => {
    // Update company info from the billing profile
    if (billingProfileData && billingProfileData.properties) {
      const props = billingProfileData.properties;
      setCompanyInfo({
        name: props.company_name?.value || companyInfo.name,
        address: props.address_line1?.value || companyInfo.address,
        city: props.address_line2?.value || companyInfo.city,
        vatNumber: props.vat_number?.value || companyInfo.vatNumber,
        bceNumber: props.bce_number?.value || companyInfo.bceNumber,
        iban: props.iban?.value || companyInfo.iban,
        bic: props.bic?.value || companyInfo.bic
      });
    }
    
    // Call the billing profile change handler with the profile ID
    if (onBillingProfileChange && billingProfileData?._id) {
      onBillingProfileChange(billingProfileData._id);
    }
    
    setHasUnsavedChanges(true);
    setCompanyDialogOpen(false); // Close the dialog after applying changes
  };
  
  // Handle invoice details save
  const handleInvoiceDetailsSave = () => {
    setInvoiceData({
      ...invoiceData,
      invoiceNumber: invoiceDetailsForm.invoiceNumber,
      invoiceDate: invoiceDetailsForm.invoiceDate,
      dueDate: invoiceDetailsForm.dueDate,
      invoiceStatus: invoiceDetailsForm.status,
      bankDetails: {
        ...invoiceData.bankDetails,
        reference: invoiceDetailsForm.reference
      },
      communication: invoiceDetailsForm.reference // Also update the communication field for compatibility
    });
    
    // Call the status change handler if provided
    if (onStatusChange && invoiceDetailsForm.status !== invoiceData.invoiceStatus) {
      onStatusChange(invoiceDetailsForm.status);
    }
    
    setInvoiceDetailsDialogOpen(false);
    setHasUnsavedChanges(true);
  };
  
  // Form state for invoice details
  const [invoiceDetailsForm, setInvoiceDetailsForm] = useState({
    invoiceNumber: invoiceData.invoiceNumber,
    invoiceDate: invoiceData.invoiceDate,
    dueDate: invoiceData.dueDate,
    reference: invoiceData.bankDetails?.reference || "",
    status: invoiceData.invoiceStatus || "Brouillon"
  });

  // Format date for display
  const formatDateForDisplay = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return format(date, 'dd/MM/yyyy');
    } catch (e) {
      return dateStr;
    }
  };

  // Handle dialog close
  const handleCloseCompanyDialog = () => {
    setCompanyDialogOpen(false);
  };

  return (
    <Paper elevation={3} sx={{ p: 2, mb: 3, position: 'relative', overflow: 'hidden' }}>
      {/* Status watermark */}
      {showWatermark && (
        <Typography
          variant="h1"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-30deg)',
            color: 'rgba(128, 128, 128, 0.15)',
            fontWeight: 'bold',
            fontSize: '4rem',
            zIndex: 0,
            pointerEvents: 'none',
            letterSpacing: '0.1em',
            textTransform: 'uppercase'
          }}
        >
          {watermarkText}
        </Typography>
      )}

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={2}>
          {/* Left side: Company Information */}
          <Grid item xs={12} md={6}>
            <Box 
              sx={{ 
                border: '1px solid transparent', 
                p: 1.5, 
                borderRadius: 1,
                transition: 'all 0.2s ease',
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  borderColor: 'primary.main',
                  border: '1px dashed primary.main',
                  cursor: 'pointer',
                  '& .editIcon': {
                    color: 'primary.main',
                    opacity: 1
                  }
                }
              }}
              onClick={() => viewMode !== 'preview' && setCompanyDialogOpen(true)}
            >
              <Box position="absolute" top={8} right={8}>
                {viewMode !== 'preview' && (
                  <Tooltip title="Éditer vos coordonnées">
                    <IconButton 
                      size="small" 
                      className="editIcon"
                      sx={{ 
                        opacity: 0.6,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                {companyInfo.name}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.3 }}>{companyInfo.address}</Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.3 }}>{companyInfo.city}</Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.3 }}>TVA {companyInfo.vatNumber}</Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.3 }}>BCE {companyInfo.bceNumber}</Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.3 }}>IBAN {companyInfo.iban}</Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.3 }}>BIC {companyInfo.bic}</Typography>
            </Box>
          </Grid>

          {/* Right side: Client Information */}
          <Grid item xs={12} md={6}>
            <Box 
              sx={{ 
                border: '1px solid transparent', 
                p: 1.5, 
                borderRadius: 1,
                transition: 'all 0.2s ease',
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  borderColor: 'primary.main',
                  border: '1px dashed primary.main',
                  cursor: 'pointer',
                  '& .editIcon': {
                    color: 'primary.main',
                    opacity: 1
                  }
                }
              }}
              onClick={() => viewMode !== 'preview' && setIsClientDialogOpen(true)}
            >
              <Box position="absolute" top={8} right={8}>
                {viewMode !== 'preview' && (
                  <Tooltip title="Éditer les coordonnées client">
                    <IconButton 
                      size="small" 
                      className="editIcon"
                      sx={{ 
                        opacity: 0.6,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              
              {invoiceData.clientDetails.name ? (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {invoiceData.clientDetails.name}
                  </Typography>
                  {invoiceData.clientDetails.companyName && invoiceData.clientDetails.fullName && (
                    <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                      {invoiceData.clientDetails.fullName}
                    </Typography>
                  )}
                  {!invoiceData.clientDetails.fullName && invoiceData.clientDetails.firstName && (
                    <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                      {invoiceData.clientDetails.firstName}
                    </Typography>
                  )}
                  {!invoiceData.clientDetails.fullName && invoiceData.clientDetails.lastName && (
                    <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                      {invoiceData.clientDetails.lastName}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ lineHeight: 1.3 }}>{invoiceData.clientDetails.address}</Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.3 }}>{invoiceData.clientDetails.city}</Typography>
                  {invoiceData.clientDetails.vatNumber && (
                    <Typography variant="body2" sx={{ lineHeight: 1.3 }}>TVA {invoiceData.clientDetails.vatNumber}</Typography>
                  )}
                  {invoiceData.clientDetails.bceNumber && (
                    <Typography variant="body2" sx={{ lineHeight: 1.3 }}>BCE {invoiceData.clientDetails.bceNumber}</Typography>
                  )}
                  {invoiceData.clientDetails.email && (
                    <Typography variant="body2" sx={{ lineHeight: 1.3 }}>Email: {invoiceData.clientDetails.email}</Typography>
                  )}
                  {invoiceData.clientDetails.phone && (
                    <Typography variant="body2" sx={{ lineHeight: 1.3 }}>Tél: {invoiceData.clientDetails.phone}</Typography>
                  )}
                </>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  py: 2,
                  color: 'text.secondary'
                }}>
                  <Avatar 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      mb: 1,
                      bgcolor: 'primary.light'
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 0.5 }}>
                    Aucun client sélectionné
                  </Typography>
                  <Typography variant="body2" align="center" sx={{ mb: 1 }}>
                    Veuillez sélectionner un client pour cette facture
                  </Typography>
                  {viewMode !== 'preview' && (
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<PersonAddIcon />}
                      onClick={() => setIsClientDialogOpen(true)}
                    >
                      Sélectionner un client
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Invoice Information */}
        <Box mt={3}>
          <Box 
            sx={{ 
              display: 'flex',
              cursor: viewMode !== 'preview' ? 'pointer' : 'default',
              p: 0.5,
              transition: 'all 0.2s ease',
              borderRadius: 1,
              border: '1px solid transparent',
              position: 'relative',
              '&:hover': viewMode !== 'preview' ? {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                borderColor: 'primary.main',
                '& .editIcon': {
                  color: 'primary.main',
                  opacity: 1
                }
              } : {}
            }}
            onClick={() => viewMode !== 'preview' && setInvoiceDetailsDialogOpen(true)}
          >
            {viewMode !== 'preview' && (
              <Box position="absolute" top={8} right={8}>
                <Tooltip title="Éditer les détails de facture">
                  <IconButton 
                    size="small" 
                    className="editIcon"
                    sx={{ 
                      opacity: 0.6,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                {invoiceData.invoice_type === 'Receipt' ? 'REÇU' : 'FACTURE'} {invoiceData.invoiceNumber}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', mb: 0.25 }}>
                  <Typography variant="body2" sx={{ width: 70, fontWeight: 'medium' }}>Date</Typography>
                  <Typography variant="body2">{formatDateForDisplay(invoiceData.invoiceDate)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', mb: 0.25 }}>
                  <Typography variant="body2" sx={{ width: 70, fontWeight: 'medium' }}>Échéance</Typography>
                  <Typography variant="body2">{formatDateForDisplay(invoiceData.dueDate)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', mb: 0.25 }}>
                  <Typography variant="body2" sx={{ width: 70, fontWeight: 'medium' }}>Référence</Typography>
                  <Typography variant="body2">{invoiceData.bankDetails?.reference || invoiceData.communication || ""}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Company Information Dialog */}
      <Dialog 
        open={companyDialogOpen} 
        onClose={handleCloseCompanyDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            overflowY: 'visible'
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          PROFIL DE FACTURATION
        </DialogTitle>
        <DialogContent sx={{ pt: 4, px: 3, pb: 3 }}>
          <BillingProfileForm 
            onSaveSuccess={handleCompanyProfileSave}
            buttonText="Appliquer"
            onCancel={handleCloseCompanyDialog}
          />
        </DialogContent>
      </Dialog>

      {/* Invoice Details Dialog */}
      <Dialog 
        open={invoiceDetailsDialogOpen} 
        onClose={() => setInvoiceDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Détails de la Facture</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Numéro de Facture"
                value={invoiceDetailsForm.invoiceNumber}
                onChange={(e) => setInvoiceDetailsForm({...invoiceDetailsForm, invoiceNumber: e.target.value})}
                margin="dense"
                disabled
              />
              <Typography variant="caption">Numéro généré automatiquement</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date de Facture"
                type="date"
                value={invoiceDetailsForm.invoiceDate}
                onChange={async (e) => {
                  const newDate = e.target.value;
                  setInvoiceDetailsForm(prev => ({ ...prev, invoiceDate: newDate }));
                  try {
                    const response = await sendRequest({
                      apiPath: `invoicing/document/next-number?document_type=facture&date=${newDate}`,
                      type: 'GET'
                    });
                    if (response && response.success) {
                      setInvoiceDetailsForm(prev => ({
                        ...prev,
                        invoiceNumber: response.full_number,
                        document_segment: response.segment,
                        document_number: response.next_number
                      }));
                    } else {
                      console.error('Failed to fetch next invoice number:', response);
                    }
                  } catch (err) {
                    console.error('Error fetching next invoice number:', err);
                  }
                }}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date d'Échéance"
                type="date"
                value={invoiceDetailsForm.dueDate}
                onChange={(e) => setInvoiceDetailsForm({...invoiceDetailsForm, dueDate: e.target.value})}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Référence"
                value={invoiceDetailsForm.reference}
                onChange={(e) => setInvoiceDetailsForm({...invoiceDetailsForm, reference: e.target.value})}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="status-label">État de la Facture</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  value={invoiceDetailsForm.status}
                  label="État de la Facture"
                  onChange={(e) => setInvoiceDetailsForm({...invoiceDetailsForm, status: e.target.value})}
                >
                  {invoiceStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceDetailsDialogOpen(false)}>Annuler</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleInvoiceDetailsSave}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default InvoiceHeader;